import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authService, ClientLogoutReason } from '../services/auth/authService';
import { getOnboardingStatus } from '../services/doctor-onboarding/doctorOnboardingService';
import { getErrorCodeFromAxios } from '@zivo/error-handling';
import { emitApiUiEvent } from '../services/api/apiEvents';
import { useAuth } from './AuthContext';

export type OnboardingStatusPayload = Awaited<ReturnType<typeof getOnboardingStatus>>;

const CACHE_TTL_MS = 5 * 60 * 1000;

function computeRedirectPath(s: OnboardingStatusPayload | null): string | null {
  if (!s) return null;
  const statusVal = (s.onboarding_status || '').toLowerCase();
  if (statusVal === 'approved') return null;
  if (statusVal === 'submitted' || statusVal === 'rejected') return '/doctor-review-status';
  return '/doctor-onboarding';
}

type OnboardingStatusContextValue = {
  loading: boolean;
  redirectTo: string | null;
  status: OnboardingStatusPayload | null;
  /** Bypass cache and refetch (e.g. after onboarding submit). Resolves with latest status after fetch. */
  refresh: () => Promise<OnboardingStatusPayload | null>;
};

const OnboardingStatusContext = createContext<OnboardingStatusContextValue | null>(null);

export function OnboardingStatusProvider({ children }: { children: React.ReactNode }) {
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<OnboardingStatusPayload | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [forceRefresh, setForceRefresh] = useState(false);
  const refreshWaiters = useRef<((v: OnboardingStatusPayload | null) => void)[]>([]);

  const statusRef = useRef(status);
  const fetchedAtRef = useRef(fetchedAt);
  const forceRefreshRef = useRef(forceRefresh);
  statusRef.current = status;
  fetchedAtRef.current = fetchedAt;
  forceRefreshRef.current = forceRefresh;

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setStatus(null);
      setFetchedAt(null);
      setRedirectTo(null);
      setLoading(false);
      setForceRefresh(false);
      refreshWaiters.current.splice(0).forEach((w) => w(null));
      return;
    }

    let cancelled = false;

    const run = async () => {
      const now = Date.now();
      const cached = statusRef.current;
      const at = fetchedAtRef.current;
      const mustFetch = forceRefreshRef.current;
      const cacheOk =
        !mustFetch &&
        cached != null &&
        at != null &&
        now - at < CACHE_TTL_MS;

      if (cacheOk) {
        setRedirectTo(computeRedirectPath(cached));
        setLoading(false);
        return;
      }

      setLoading(true);
      let refreshOutcome: OnboardingStatusPayload | null = null;
      try {
        const s = await getOnboardingStatus();
        if (cancelled) return;
        refreshOutcome = s;
        setStatus(s);
        setFetchedAt(Date.now());
        setRedirectTo(computeRedirectPath(s));
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosStatus = (err as { response?: { status?: number } })?.response?.status;
        if (axiosStatus === 401 || axiosStatus === 403) {
          try {
            await authService.refreshAccessToken();
            if (cancelled) return;
            setRedirectTo(null);
            setFetchedAt(Date.now());
            return;
          } catch (refreshErr: unknown) {
            const code = getErrorCodeFromAxios(refreshErr);
            if (
              code === 'refresh_token_expired' ||
              code === 'refresh_token_revoked' ||
              code === 'user_disabled' ||
              code === 'token_invalid' ||
              code === 'token_missing'
            ) {
              await authService.clearTokens({ reason: ClientLogoutReason.OnboardingRefreshFailed });
              if (cancelled) return;
              setRedirectTo('/login');
              setStatus(null);
              setFetchedAt(null);
              return;
            }
            emitApiUiEvent({
              type: 'session_issue',
              message:
                'Your session could not be verified. Please try again or log in again.',
            });
            setRedirectTo(null);
          }
        } else {
          setRedirectTo(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (forceRefreshRef.current) {
            setForceRefresh(false);
          }
          const wait = refreshWaiters.current.splice(0);
          wait.forEach((w) => w(refreshOutcome));
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, forceRefresh]);

  const refresh = useCallback(() => {
    return new Promise<OnboardingStatusPayload | null>((resolve) => {
      refreshWaiters.current.push(resolve);
      setForceRefresh(true);
    });
  }, []);

  const value = useMemo<OnboardingStatusContextValue>(
    () => ({
      loading,
      redirectTo,
      status,
      refresh,
    }),
    [loading, redirectTo, status, refresh]
  );

  return (
    <OnboardingStatusContext.Provider value={value}>{children}</OnboardingStatusContext.Provider>
  );
}

export function useOnboardingStatus(): OnboardingStatusContextValue {
  const ctx = useContext(OnboardingStatusContext);
  if (!ctx) {
    throw new Error('useOnboardingStatus must be used within OnboardingStatusProvider');
  }
  return ctx;
}
